import { Assert } from "../Assert";
import { PartialNullable } from "../Extensions";
import { ObjectHelper } from "../Helpers/ObjectHelper";
import { StringHelper } from "../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../JNative";
import { IRadObject, Property, RadObject, RadObjectPropertyValues } from "../RadObject";
import { ContainerControlConstructor, IContainerControl } from "./Controls/ContainerControl";
import { IControl } from "./Controls/Control";
import { NAMESPACE } from "./_Namespace";

const
    DRAGGABLE_BORDER_WIDTH = 1,
    ENABLE_DRAG_DEBUG = false;

enum DragDropManagerInputTypeEnum
{
    mouse = "mouse",
    touch = "touch",
}

export interface IDragDropManagerOptions
{
    cssClass?: string,
    getDraggableContainer?: () => JNative<HTMLElement> | undefined;
    inhibitHorizontal?: boolean;
    inhibitScroll?: boolean;
    inhibitVertical?: boolean;
    onDraggableCreated?: ($$draggable: JNative<HTMLElement>, $$clone: JNative<HTMLElement>) => JNative<HTMLElement>;
    requiredCssClasses?: string[];
    singleItem?: boolean;
}

export interface IDragDropAreaChangedEventData
{
    $$item: JNative<HTMLElement>;
    $$area: JNative<HTMLElement>;
    $$areaOriginal: JNative<HTMLElement>;
}

export interface IDragDropEndedEventData
{
    $$item: JNative<HTMLElement>;
    $$area: JNative<HTMLElement>;
}

export interface IDragDropStartedEventData
{
    $$item?: JNative<HTMLElement>;
    $$area: JNative<HTMLElement>;
    $$draggable: JNative<HTMLElement>;
}

export type DragDropManagerPropertyValues =
    RadObjectPropertyValues
    & {
    };

export type DragDropManagerProperties = {
    [name in keyof DragDropManagerPropertyValues]: Property<DragDropManagerPropertyValues[name]>
};

export interface IDragDropManager extends IRadObject, DragDropManagerProperties
{
}

export class DragDropManager extends RadObject implements IDragDropManager
{
    // #region Static

    public static override classNamespace = NAMESPACE + "DragDropManager";
    public static override className = "DragDropManager";

    // #region Properties

    public static instance = new DragDropManager();

    // #endregion

    // #region Constructor

    static {
        $$(document).on("click", ".rad-drag-drop-handle", (evt: IJNativeEvent) =>
        {
            evt.preventDefault();
            evt.stopPropagation();
        });
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<DragDropManagerPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected ENABLE_DRAG_DEBUG = ENABLE_DRAG_DEBUG;

    protected $$area?: JNative<HTMLElement>;
    protected $$debugPanel?: JNative<HTMLElement>;
    protected $$document: JNative<Document> = $$(document);
    protected $$draggable?: JNative<HTMLElement>;
    protected $$elementForSizing?: JNative<HTMLElement>;
    protected $$item?: JNative<HTMLElement>;
    protected $$page?: JNative<HTMLElement>;
    protected $$pageElementToScroll?: JNative<HTMLElement>;
    protected control?: IControl;
    protected debugData: {
        calculatedBoundaryActivationDistances: { height: number, width: number },
        coords: { x: number, y: number },
        pageElementScrollPosition: { left: number, top: number },
        scollBoundaryAreaSize: { height: number, width: number },
        targetControl?: IControl,
        targetControlAcceptsDragItem?: boolean,
    };
    protected initialMouseOffset?: { left: number, top: number };
    protected initialTargetOffset?: { left: number, top: number };
    protected inputType?: DragDropManagerInputTypeEnum;
    protected itemOptions?: IDragDropManagerOptions;
    protected lastMovementCoords?: { x: number, y: number };
    protected pageOffset: { left: number, top: number };
    protected scrollAmount = 30;
    protected scrollBoundaryActivations: { bottom: boolean, left: boolean, right: boolean, top: boolean };
    protected scrollDuration = 10;
    protected scrollHorizontalInProgress = false;
    protected scrollTimerHandle?: number;
    protected scrollTimerInterval = 50;
    protected scrollVerticalInProgress = false;

    // #endregion

    // #region Constructor

    private constructor(propertyValues?: PartialNullable<DragDropManagerPropertyValues>)
    {
        super(propertyValues);

        this.debugData = {
            calculatedBoundaryActivationDistances: { height: 0, width: 0 },
            coords: { x: 0, y: 0 },
            pageElementScrollPosition: { left: 0, top: 0 },
            scollBoundaryAreaSize: { height: 0, width: 0 },
        };

        this.scrollBoundaryActivations = {
            bottom: false,
            left: false,
            right: false,
            top: false,
        }

        this.pageOffset = {
            left: 0,
            top: 0,
        }

        if (this.ENABLE_DRAG_DEBUG)
        {
            this.$$debugPanel =
                JNativeStatic.create$$({ nodeName: "div", attributes: { "style": "background-color: white; width: 400px; height:350px; position: fixed; top:150px; right:50px; z-index: 9999; border:1px solid;" } })
                    .appendTo(document.body);
        }
    }

    // #endregion

    // #region Public Methods

    public createDragDropAreas($$elements?: JNative<HTMLElement>, options?: IDragDropManagerOptions)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.addClass("rad-drag-drop-area");
        $$elements.forEach(item => { JNativeStatic.data(item, "dragDropManager:options", options); });

        if (options)
        {
            if (options.cssClass)
                $$elements.addClass(options.cssClass);

            if (options.singleItem)
                $$elements.addClass("rad-drag-drop-area-single-item");
        }
    }
    public createDragDropHandles($$elements?: JNative<HTMLElement>, options?: IDragDropManagerOptions)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.addClass("rad-drag-drop-handle");
        $$elements.on("mousedown", { self: this }, this.handleMouseDown);
        $$elements.on("touchstart", { self: this }, this.handleTouchStart);
        $$elements.forEach(item => { JNativeStatic.data(item, "dragDropManager:options", options); });
    }
    public createDragDropItems($$elements?: JNative<HTMLElement>, options?: IDragDropManagerOptions)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.addClass("rad-drag-drop-item");
        $$elements.forEach(item => { JNativeStatic.data(item, "dragDropManager:options", options); });
    }
    public destroyDragDropAreas($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        const
            options: IDragDropManagerOptions = $$elements.data("dragDropManager:options");

        $$elements.removeClass("rad-drag-drop-area");

        if (options)
        {
            if (options.cssClass)
                $$elements.removeClass(options.cssClass);

            if (options.singleItem)
                $$elements.removeClass("rad-drag-drop-area-single-item");
        }

        $$elements.removeData("dragDropManager:options");
    }
    public destroyDragDropHandles($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.removeClass("rad-drag-drop-handle");
        $$elements.off("mousedown", { self: self }, this.handleMouseDown);
        $$elements.off("touchstart", { self: self }, this.handleTouchStart);
        $$elements.removeData("dragDropManager:options");
    }
    public destroyDragDropItems($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.removeClass("rad-drag-drop-item");
        $$elements.removeData("dragDropManager:options");
    }
    public destroyDragDrops($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.find("[id]").addBack("[id]").removeAttr("id");
        this.destroyDragDropAreas($$elements.find(".rad-drag-drop-area").addBack(".rad-drag-drop-area"));
        this.destroyDragDropItems($$elements.find(".rad-drag-drop-item").addBack(".rad-drag-drop-item"));
        this.destroyDragDropHandles($$elements.find(".rad-drag-drop-handle").addBack(".rad-drag-drop-handle"));
    }
    public disableDragDropAreas($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.addClass("rad-drag-drop-area-disabled");
    }
    public disableDragDropHandles($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.off("mousedown", { self: self }, this.handleMouseDown);
        $$elements.off("touchstart", { self: self }, this.handleTouchStart);

        if ($$elements.data("dragDropManager:options"))
            $$elements.addClass("rad-drag-drop-handle-disabled");
    }
    public disableDragDropItems($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        if ($$elements.data("dragDropManager:options"))
            $$elements.addClass("rad-drag-drop-item-disabled");
    }
    public disableDragDrops($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.find("[id]").addBack("[id]").removeAttr("id");
        this.disableDragDropAreas($$elements.find(".rad-drag-drop-area").addBack(".rad-drag-drop-area"));
        this.disableDragDropItems($$elements.find(".rad-drag-drop-item").addBack(".rad-drag-drop-item"));
        this.disableDragDropHandles($$elements.find(".rad-drag-drop-handle").addBack(".rad-drag-drop-handle"));
    }
    public enableDragDropAreas($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.removeClass("rad-drag-drop-area-disabled");
    }
    public enableDragDropHandles($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.removeClass("rad-drag-drop-handle-disabled");
        $$elements.on("mousedown", { self: this }, this.handleMouseDown);
        $$elements.on("touchstart", { self: this }, this.handleTouchStart);
    }
    public enableDragDropItems($$elements?: JNative<HTMLElement>)
    {
        Assert.isNotNullOrUndefined($$elements, "$$elements");

        $$elements.removeClass("rad-drag-drop-item-disabled");
    }

    // #endregion

    // #region Private Methods

    private doBoundaryScrollOnInterval()
    {
        if (this.itemOptions?.inhibitScroll)
            return;

        Assert.isNotNullOrUndefined(this.$$pageElementToScroll, "this.$$pageElementToScroll");

        const
            $$pageElementToScrollScrollTop = this.$$pageElementToScroll.scrollTop(),
            $$pageElementToScrollScrollLeft = this.$$pageElementToScroll.scrollLeft(),
            $$documentHeight = this.$$document.height(),
            $$pageElementToScrollWidth = this.$$pageElementToScroll.width();

        Assert.isNotNullOrUndefined($$pageElementToScrollScrollTop, "$$pageElementToScrollScrollTop");
        Assert.isNotNullOrUndefined($$pageElementToScrollScrollLeft, "$$pageElementToScrollScrollLeft");
        Assert.isNotNullOrUndefined($$documentHeight, "$$documentHeight");
        Assert.isNotNullOrUndefined($$pageElementToScrollWidth, "$$pageElementToScrollWidth");

        if (this.scrollBoundaryActivations)
        {
            const
                scrollAdjustmentAmount = this.scrollBoundaryActivations.top || this.scrollBoundaryActivations.left ? 1 - this.scrollAmount : this.scrollAmount;

            if (this.scrollBoundaryActivations.top || this.scrollBoundaryActivations.bottom)
            {
                if (!this.scrollVerticalInProgress)
                {
                    if (
                        this.scrollBoundaryActivations.top && $$pageElementToScrollScrollTop + scrollAdjustmentAmount >= 0
                        || this.scrollBoundaryActivations.bottom && $$pageElementToScrollScrollTop + scrollAdjustmentAmount <= $$documentHeight
                    )
                    {
                        this.scrollVerticalInProgress = true;

                        const
                            lastScrollTop = this.$$pageElementToScroll.scrollTop();

                        this.$$pageElementToScroll
                            .animate(
                                {
                                    scrollTop: $$pageElementToScrollScrollTop + scrollAdjustmentAmount
                                },
                                this.scrollDuration,
                                "linear"
                            )
                            .promise()
                            .finally(() =>
                            {
                                this.scrollVerticalInProgress = false;
                                if (lastScrollTop !== $$pageElementToScrollScrollTop)
                                {
                                    Assert.isNotNullOrUndefined(this.lastMovementCoords, "this.lastMovementCoords");
                                    this.inputMove({ x: this.lastMovementCoords.x, y: this.lastMovementCoords.y });
                                }
                            });
                    }
                }
            }

            if (this.scrollBoundaryActivations.left || this.scrollBoundaryActivations.right)
            {
                if (!this.scrollHorizontalInProgress)
                {
                    if (
                        this.scrollBoundaryActivations.left && $$pageElementToScrollScrollLeft + scrollAdjustmentAmount >= 0
                        || this.scrollBoundaryActivations.right && $$pageElementToScrollWidth + $$pageElementToScrollScrollLeft + scrollAdjustmentAmount <= $$pageElementToScrollWidth
                    )
                    {
                        this.scrollHorizontalInProgress = true;

                        const
                            lastScrollLeft = $$pageElementToScrollScrollLeft;

                        this.$$pageElementToScroll
                            .animate(
                                {
                                    scrollLeft: $$pageElementToScrollScrollLeft + scrollAdjustmentAmount
                                },
                                this.scrollDuration,
                                "linear"
                            )
                            .promise()
                            .finally(() =>
                            {
                                this.scrollHorizontalInProgress = false;
                                if (lastScrollLeft !== $$pageElementToScrollScrollLeft)
                                {
                                    Assert.isNotNullOrUndefined(this.lastMovementCoords, "this.lastMovementCoords");
                                    this.inputMove({ x: this.lastMovementCoords.x, y: this.lastMovementCoords.y });
                                }
                            });
                    }
                }
            }
        }
    }
    private getClassDraggableDataFromElement($$target: JNative<HTMLElement>)
    {
        const
            $$targetControlElement = $$target?.closest(".rad-control");

        if (!$$targetControlElement)
            return null;

        const
            targetControl = $$targetControlElement.radControl();

        Assert.isAssignableTo<IContainerControl>(targetControl, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ContainerControl.ContainerControl");

        const
            targetControlClass = Object.getPrototypeOf(targetControl).constructor as ContainerControlConstructor;

        return targetControlClass;
    }
    private getCoords(evt: MouseEvent | TouchEvent)
    {
        if (evt instanceof MouseEvent)
            return { x: evt.pageX, y: evt.pageY };

        if (evt instanceof TouchEvent)
        {
            const
                input = evt.touches[0] || evt.changedTouches[0];

            return { x: input.pageX, y: input.pageY };
        }

        throw new Error("Unsupported");
    }
    private handleMouseDown(evt: IJNativeEvent<MouseEvent>): void
    {
        const
            self = evt.data.self as this;

        if (!(evt.currentTarget instanceof HTMLElement))
            return;

        const
            $$target = $$(evt.currentTarget);

        if (!self.inputType && self.shouldStart($$target))
        {
            self.inputType = DragDropManagerInputTypeEnum.mouse;
            $$(document).on("mousemove touchmove", { self }, self.onInputMove);
            $$(document).on("mousemove", { self }, self.onMouseMove);
            $$(document).on("mouseup", { self }, self.onMouseUp);
            self.inputStart($$target, self.getCoords(evt.originalEvent));
        }
    }
    private handleTouchStart(evt: IJNativeEvent<TouchEvent>): void
    {
        const
            self = evt.data.self as this;

        if (!(evt.currentTarget instanceof HTMLElement))
            return;

        const
            $$target = $$(evt.currentTarget);

        if (!self.inputType && self.shouldStart($$target))
        {
            self.inputType = DragDropManagerInputTypeEnum.touch;
            $$(document).on("mousemove touchmove", { self }, self.onInputMove);
            $$(document).on("touchmove", { self }, self.onTouchMove);
            $$(document).on("touchend", { self }, self.onTouchEnd);
            self.inputStart($$target, self.getCoords(evt.originalEvent));
        }
    }
    private inputEnd()
    {
        if (!this.$$item || !this.$$area || !this.$$draggable)
            return;

        this.$$item.css({ opacity: StringHelper.empty });
        this.$$draggable.remove();
        this.$$draggable.empty();

        $$("html").removeClass("rad-drag-drop-active");
        this.$$item.radControl()?.trigger("dragDropEnded", { $$area: this.$$area });
        this.triggerHandler("dragDropEnded", { $$item: this.$$item, $$area: this.$$area });

        clearInterval(this.scrollTimerHandle);
        this.scrollHorizontalInProgress = false;
        this.scrollVerticalInProgress = false;
        this.$$item = undefined;
        this.control = undefined;
        this.$$area = undefined;
        this.$$draggable = undefined;
        this.$$page = undefined;
        //$$pageElementToScroll = null;
        this.$$elementForSizing = undefined;

        $$(document).off("mousemove touchmove", { self: this }, this.onInputMove);

        $$(document).off("mousemove", { self: this }, this.onMouseMove);
        $$(document).off("mouseup", { self: this }, this.onMouseUp);

        $$(document).off("touchmove", { self: this }, this.onTouchMove);
        $$(document).off("touchend", { self: this }, this.onTouchEnd);
    }
    private inputMove(coords: { x: number, y: number })
    {
        if (!this.$$item || !this.$$area)
            return;

        Assert.isNotNullOrUndefined(this.initialTargetOffset, "initialTargetOffset");
        Assert.isNotNullOrUndefined(this.initialMouseOffset, "initialMouseOffset");

        if (!this.$$draggable)
        {
            this.$$draggable = JNativeStatic.create$$({ nodeName: "div", attributes: { "class": "rad-drag-drop-draggable" } });

            const
                itemElement = this.$$item.get(0);

            Assert.isNotNullOrUndefined(itemElement, "itemElement");

            const
                $$clone = $$(itemElement.outerHTML);

            this.disableDragDrops($$clone);
            $$clone.css({ marginTop: 0 });
            this.$$draggable.css({
                position: "absolute",
                "z-index": 9999,
                width: this.$$item.outerWidth() + 2 * DRAGGABLE_BORDER_WIDTH,
                height: this.$$item.outerHeight() + 2 * DRAGGABLE_BORDER_WIDTH
            });
            this.$$draggable.append($$clone);

            if (this.itemOptions?.onDraggableCreated)
                this.$$draggable = this.itemOptions.onDraggableCreated(this.$$draggable, $$clone);

            Assert.isNotNullOrUndefined(this.$$draggable, "this.$$draggable");

            this.$$draggable.appendTo(this.$$page);

            this.$$item.css({ opacity: 0.3 });

            if (this.control)
                this.$$draggable.addClass(Object.getPrototypeOf(this.control).constructor.dragDropDraggableCssClass);

            this.$$draggable.css(this.initialTargetOffset);

            $$("html").addClass("rad-drag-drop-active rad-drag-drop-overflow-flag");
            this.$$item.radControl()?.trigger("dragDropStarted", { $$area: this.$$area, $$draggable: this.$$draggable });
            this.triggerHandler("dragDropStarted", { $$item: this.$$item, $$area: this.$$area, $$draggable: this.$$draggable });

            this.scrollTimerHandle = window.setInterval(() => { this.doBoundaryScrollOnInterval(); }, this.scrollTimerInterval);
            setTimeout(() => { $$("html").removeClass("rad-drag-drop-overflow-flag"); }, 100);
        }

        const
            newPosition: { left?: number, top?: number } = {
            };

        if (!this.itemOptions || !this.itemOptions.inhibitHorizontal)
            newPosition.left = this.initialTargetOffset.left - (this.initialMouseOffset.left - coords.x);

        if (!this.itemOptions || !this.itemOptions.inhibitVertical)
            newPosition.top = this.initialTargetOffset.top - (this.initialMouseOffset.top - coords.y);

        this.$$draggable.css(newPosition);

        const
            $$pageElementToScrollScrollTop = this.$$pageElementToScroll?.scrollTop(),
            $$pageElementToScrollScrollLeft = this.$$pageElementToScroll?.scrollLeft(),
            $$elementForSizingWidth = this.$$elementForSizing?.width(),
            $$elementForSizingHeight = this.$$elementForSizing?.height();

        Assert.isNotNullOrUndefined($$pageElementToScrollScrollTop, "$$pageElementToScrollScrollTop");
        Assert.isNotNullOrUndefined($$pageElementToScrollScrollLeft, "$$pageElementToScrollScrollLeft");
        Assert.isNotNullOrUndefined($$elementForSizingWidth, "$$elementForSizingWidth");
        Assert.isNotNullOrUndefined($$elementForSizingHeight, "$$elementForSizingHeight");

        const
            pageElementScrollPosition = { top: $$pageElementToScrollScrollTop, left: $$pageElementToScrollScrollLeft },
            scollBoundaryAreaSize = { width: $$elementForSizingWidth, height: $$elementForSizingHeight },
            boundaryActivationDistancePercentage = 10,
            calculatedBoundaryActivationDistances = { width: scollBoundaryAreaSize.width / 100 * boundaryActivationDistancePercentage, height: scollBoundaryAreaSize.height / 100 * boundaryActivationDistancePercentage };

        this.lastMovementCoords = { x: coords.x, y: coords.y };

        Assert.isNotNullOrUndefined(this.pageOffset, "pageOffset");

        this.scrollBoundaryActivations = {
            top: coords.y - this.pageOffset.top <= calculatedBoundaryActivationDistances.height,
            left: coords.x - this.pageOffset.left <= calculatedBoundaryActivationDistances.width,
            bottom: coords.y - this.pageOffset.top >= scollBoundaryAreaSize.height - calculatedBoundaryActivationDistances.height,
            right: coords.x - this.pageOffset.left >= scollBoundaryAreaSize.width - calculatedBoundaryActivationDistances.width
        };

        this.debugData.calculatedBoundaryActivationDistances = calculatedBoundaryActivationDistances;
        this.debugData.coords = coords;
        this.debugData.pageElementScrollPosition = pageElementScrollPosition;
        this.debugData.scollBoundaryAreaSize = scollBoundaryAreaSize;
        this.updateDebugPanel();
    }
    private inputStart($$target: JNative<HTMLElement>, coords: { x: number, y: number })
    {
        this.$$item = $$target.closest(".rad-drag-drop-item");
        this.itemOptions = this.$$item.data("dragDropManager:options");
        this.control = this.$$item.radControl();
        this.$$area = this.$$item.parent();

        const
            itemOffset = this.$$item.offset();

        Assert.isNotNullOrUndefined(itemOffset, "itemOffset");

        if (this.itemOptions?.getDraggableContainer)
        {
            this.$$page = this.itemOptions.getDraggableContainer();
            this.$$pageElementToScroll = this.$$page?.first();
        }
        else
        {
            this.$$page = this.$$area.closest(".rad-control-page");
            this.$$pageElementToScroll = this.$$page.find(".rad-control-container-wrapper").first();
        }

        this.pageOffset = this.$$page?.offset() ?? { left: 0, top: 0 };

        this.$$elementForSizing = this.$$page;

        const
            $$pageElementToScrollScrollLeft = this.$$pageElementToScroll?.scrollLeft();

        Assert.isNotNullOrUndefined($$pageElementToScrollScrollLeft, "$$pageElementToScrollScrollLeft");

        this.initialTargetOffset = { top: itemOffset.top - this.pageOffset.top, left: itemOffset.left - this.pageOffset.left + $$pageElementToScrollScrollLeft };
        this.initialMouseOffset = { top: coords.y + DRAGGABLE_BORDER_WIDTH, left: coords.x + DRAGGABLE_BORDER_WIDTH };
    }
    private isDragItemAllowedInCurrentDragTarget($$target: JNative<HTMLElement>)
    {
        if (!this.$$item || !$$target)
            return false;

        const
            targetContainerControlConstructor = this.getClassDraggableDataFromElement($$target);

        Assert.isNotNullOrUndefined(targetContainerControlConstructor, "targetContainerControlConstructor");

        const
            itemClass = Object.getPrototypeOf(this.$$item.radControl()).constructor;

        let
            targetAllows = true;

        const
            allowedChildControlClasses = targetContainerControlConstructor.getAllowedChildControlClasses();

        if (allowedChildControlClasses.length)
            targetAllows = allowedChildControlClasses.some(item => ObjectHelper.classInheritsClass(itemClass, item));

        return targetAllows;
    }
    private isMouseOverPadding($$target: JNative<HTMLElement>, coords: { x: number, y: number }): boolean
    {
        if (!$$target || !coords)
            return false;

        const
            padding = {
                left: parseInt($$target.css("padding-left") as string),
                right: parseInt($$target.css("padding-right") as string),
                top: parseInt($$target.css("padding-top") as string),
                bottom: parseInt($$target.css("padding-bottom") as string)
            };

        if (padding.left <= 0 && padding.right <= 0 && padding.top <= 0 && padding.bottom <= 0)
            return false;

        const
            offset = $$target.offset();

        Assert.isNotNullOrUndefined(offset, "offset");

        const
            size = {
                width: $$target.outerWidth(),
                height: $$target.outerHeight()
            };

        if (padding.top > 0 && coords.y < offset.top + padding.top)
            return true;
        else if (padding.left > 0 && coords.x < offset.left + padding.left)
            return true;
        else if (padding.right > 0 && coords.x > offset.left + size.width - padding.right)
            return true;
        else if (padding.bottom > 0 && coords.y > offset.top + size.height - padding.bottom)
            return true;

        return false;
    }
    private processDragDropAreaChange(evt: MouseEvent | TouchEvent, target: HTMLElement)
    {
        Assert.isNotNullOrUndefined(this.$$item, "this.$$item");

        const
            $$newArea = $$(target),
            areaOptions = $$newArea.data("dragDropManager:options"),
            itemElement = this.$$item.get(0);

        Assert.isNotNullOrUndefined(itemElement, "itemElement");

        if (this.$$item.get(0) === target || JNativeStatic.contains(itemElement, target) || $$newArea.hasClass("rad-drag-drop-area-disabled"))
            return;

        if ($$newArea.hasClass("rad-drag-drop-area-single-item") && $$newArea.find(">.rad-drag-drop-item").length !== 0)
            return;

        if (this.itemOptions?.requiredCssClasses)
        {
            if (!$$newArea.hasAnyClass(this.itemOptions.requiredCssClasses))
                return;
        }

        if (areaOptions?.acceptedCssClasses)
        {
            if (!this.$$item.hasAnyClass(areaOptions.acceptedCssClasses))
                return;
        }

        const
            coords = this.getCoords(evt);

        let
            $$insertBefore = null;

        $$newArea.find(">.rad-drag-drop-item")
            .each((index, element) =>
            {
                const
                    $$element = $$(element),
                    offset = $$element.offset();

                Assert.isNotNullOrUndefined(offset, "offset");

                if (offset.top + $$element.outerHeight(true) / 2 > coords.y)
                {
                    $$insertBefore = $$element;
                    return false;
                }
            });

        if ($$insertBefore)
            this.$$item.insertBefore($$insertBefore);
        else
            this.$$item.appendTo($$newArea);

        Assert.isNotNullOrUndefined(this.$$area, "this.$$area");

        if (!this.$$area.is($$newArea))
        {
            this.$$item.radControl()?.trigger("dragDropAreaChanged", { $$area: $$newArea, $$areaOriginal: this.$$area });
            this.triggerHandler("dragDropAreaChanged", { $$item: this.$$item, $$area: $$newArea, $$areaOriginal: this.$$area });
        }

        this.$$area = $$newArea;
    }
    private shouldStart($$target: JNative<HTMLElement>): boolean
    {
        const
            $$dragdropItem = $$target.closest(".rad-drag-drop-item"),
            $$dragDropArea = $$dragdropItem.parent();

        if ($$dragdropItem.length === 0 || $$dragdropItem.hasClass("rad-drag-drop-item-disabled"))
            return false;

        if (!$$dragDropArea || !$$dragDropArea.hasClass("rad-drag-drop-area"))
            throw new Error("Drag drop item is not a direct child of a drag drop area");

        return true;
    }
    private updateDebugPanel()
    {
        if (!this.ENABLE_DRAG_DEBUG)
            return;

        Assert.isNotNullOrUndefined(this.$$debugPanel, "this.$$debugPanel");

        const
            calculatedBoundaryActivationDistances = this.debugData.calculatedBoundaryActivationDistances,
            coords = this.debugData.coords,
            pageElementScrollPosition = this.debugData.pageElementScrollPosition,
            scollBoundaryAreaSize = this.debugData.scollBoundaryAreaSize,
            targetControl = this.debugData.targetControl,
            targetControlAcceptsDragItem = this.debugData.targetControlAcceptsDragItem;

        this.$$debugPanel.html("<span><u><b>Drag Debug</b></u></span><br><span>coords x/y: " + coords.x + ":" + coords.y + "</span>");
        this.$$debugPanel.append("<br><span>initial target offset: " + ObjectHelper.toJson(this.initialTargetOffset) + "</span>");
        this.$$debugPanel.append("<br><span>initial mouse offset: " + ObjectHelper.toJson(this.initialMouseOffset) + "</span>");
        this.$$debugPanel.append("<br><span>pageElementScrollPosition: " + ObjectHelper.toJson(pageElementScrollPosition) + "</span>");
        this.$$debugPanel.append("<br/><span>calculatedBoundaryActivationDistances: " + ObjectHelper.toJson(calculatedBoundaryActivationDistances) + "</span>");
        this.$$debugPanel.append("<br/><span>scrollBoundaryActivations: </span>");
        this.$$debugPanel.append("<br/><span>- " + (this.scrollBoundaryActivations.top ? "<b><u>top</u></b>" : "top") + " (" + (coords.y - this.pageOffset.top) + " - " + pageElementScrollPosition.top + " <= " + calculatedBoundaryActivationDistances.height + ")</span>");
        this.$$debugPanel.append("<br/><span>- " + (this.scrollBoundaryActivations.left ? "<b><u>left</u></b>" : "left") + "</span>");
        this.$$debugPanel.append("<br/><span>- " + (this.scrollBoundaryActivations.bottom ? "<b><u>bottom</u></b>" : "bottom") + " (" + (coords.y - this.pageOffset.top) + " >= " + pageElementScrollPosition.top + " + " + scollBoundaryAreaSize.height + " - " + calculatedBoundaryActivationDistances.height + ")</span>");
        this.$$debugPanel.append("<br/><span>- " + (this.scrollBoundaryActivations.right ? "<b><u>right</u></b>" : "right") + "</span>");

        if (targetControl)
            this.$$debugPanel.append("<br><span>targetting: " + Object.getPrototypeOf(targetControl).constructor.name + " - " + targetControl.controlId + ", targetControlAcceptsDragItem: " + targetControlAcceptsDragItem + "</span>");
    }

    // #endregion

    // #region Event Handlers

    protected onInputMove(evt: IJNativeEvent<MouseEvent | TouchEvent>)
    {
        const
            self = evt.data.self as this;

        if (self.inputType !== DragDropManagerInputTypeEnum.mouse && self.inputType !== DragDropManagerInputTypeEnum.touch)
            return;

        evt.preventDefault();

        const
            coords = self.getCoords(evt.originalEvent);

        coords.x = coords.x - ($$(document).scrollLeft() ?? 0);
        coords.y = coords.y - ($$(document).scrollTop() ?? 0);

        let
            $$target = $$(document.elementFromPoint(coords.x, coords.y) as HTMLElement),
            deeper = true;

        // put these back on for isMouseOverPadding
        coords.x = coords.x + ($$(document).scrollLeft() ?? 0);
        coords.y = coords.y + ($$(document).scrollTop() ?? 0);

        while (deeper)
        {
            deeper = false;

            if (!$$target.hasClass("rad-drag-drop-area"))
                deeper = true;
            else if (self.isMouseOverPadding($$target, coords))
                deeper = true;

            if ($$target.hasClass("rad-drag-drop-container"))
                deeper = false;

            if (deeper)
                $$target = $$target.parent();

            if (!$$target.length)
                return;
        }

        const
            targetControlAcceptsDragItem = self.isDragItemAllowedInCurrentDragTarget($$target);

        self.debugData.targetControl = $$target.closest(".rad-control").radControl();
        self.debugData.targetControlAcceptsDragItem = targetControlAcceptsDragItem;
        self.updateDebugPanel();

        if (!targetControlAcceptsDragItem)
            return;

        evt.stopPropagation();

        self.processDragDropAreaChange(evt.originalEvent, $$target[0]);
    }
    protected onMouseMove(evt: IJNativeEvent<MouseEvent>)
    {
        const
            self = evt.data.self as this;

        if (self.inputType === DragDropManagerInputTypeEnum.mouse)
            self.inputMove(self.getCoords(evt.originalEvent));
    }
    protected onMouseUp(evt: IJNativeEvent<MouseEvent>)
    {
        const
            self = evt.data.self as this;

        if (self.inputType === DragDropManagerInputTypeEnum.mouse)
        {
            self.inputEnd();
            delete self.inputType;
        }
    }
    protected onTouchEnd(evt: IJNativeEvent<TouchEvent>)
    {
        const
            self = evt.data.self as this;

        if (self.inputType === DragDropManagerInputTypeEnum.touch)
        {
            self.inputEnd();
            delete self.inputType;
        }
    }
    protected onTouchMove(evt: IJNativeEvent<TouchEvent>)
    {
        const
            self = evt.data.self as this;

        if (self.inputType === DragDropManagerInputTypeEnum.touch)
        {
            evt.preventDefault();
            self.inputMove(self.getCoords(evt.originalEvent));
        }
    }

    // #endregion

    // #endregion
}